import { useEffect, useMemo, useRef, useState } from 'react'
import { startToolOperation, trackToolRejection, type ToolOperation } from '@saas-pages/shared/public/runtime'
import { Button, Card, CardContent } from '@saas-pages/shared/ui'
import { recorderAnalytics } from '@/features/recorder/model/analytics'
import { ActiveTimeTracker, classifyMediaError, createCapturedMediaStream, extensionForMime, resolveRecorderMime, selectSupportedMimeType, stopStream, type RecorderError } from '@/features/recorder/model/media'
import { RecorderStatus } from '@/features/recorder/ui/RecorderStatus'
import { useLocale, useT } from '@/shared/i18n'

type Status = 'idle' | 'requesting' | 'ready' | 'recording' | 'paused' | 'finished' | 'error'
const MAX_SECONDS = 60
const MAX_DURATION_MS = MAX_SECONDS * 1000

export function CircleVideoRecorder() {
  const t = useT()
  const { locale } = useLocale()
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<RecorderError | null>(null)
  const [seconds, setSeconds] = useState(0)
  const [url, setUrl] = useState<string | null>(null)
  const [resultMime, setResultMime] = useState<string | null>(null)
  const [mirror, setMirror] = useState(true)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const outputStreamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const timerRef = useRef<number | null>(null)
  const deadlineRef = useRef<number | null>(null)
  const frameRef = useRef<number | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const chunkTypesRef = useRef<string[]>([])
  const failedRef = useRef(false)
  const resultUrlRef = useRef<string | null>(null)
  const mountedRef = useRef(true)
  const generationRef = useRef(0)
  const operationRef = useRef<ToolOperation | null>(null)
  const activeTimeRef = useRef(new ActiveTimeTracker())
  const mime = useMemo(() => typeof MediaRecorder === 'undefined' ? undefined : selectSupportedMimeType('video', MediaRecorder.isTypeSupported.bind(MediaRecorder)), [status])
  const analytics = recorderAnalytics('/krugloe-video', 'circle-video-recorder', locale)

  useEffect(() => {
    if (videoRef.current && streamRef.current && status !== 'finished') videoRef.current.srcObject = streamRef.current
  }, [status])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      generationRef.current += 1
      const recorder = recorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        failedRef.current = true
        recorder.ondataavailable = null; recorder.onerror = null; recorder.onstop = null
        operationRef.current?.error('cancelled'); operationRef.current = null
        try { recorder.stop() } catch { /* already stopping */ }
      }
      cleanup()
      if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    }
  }, [])

  function cleanup() {
    if (timerRef.current !== null) window.clearInterval(timerRef.current)
    if (deadlineRef.current !== null) window.clearTimeout(deadlineRef.current)
    timerRef.current = null; deadlineRef.current = null
    if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current)
    stopStream(outputStreamRef.current); outputStreamRef.current = null
    stopStream(streamRef.current); streamRef.current = null
  }

  async function prepare() {
    const generation = ++generationRef.current
    cleanup()
    revokeResult()
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('unsupported'); setStatus('error'); trackToolRejection(analytics, 'unsupported_input'); return
    }
    setError(null); setStatus('requesting')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1080 }, height: { ideal: 1080 }, facingMode: 'user' }, audio: true,
      })
      if (!mountedRef.current || generation !== generationRef.current) { stopStream(stream); return }
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        void videoRef.current.play().catch(() => {})
      }
    } catch (cause) {
      if (!mountedRef.current || generation !== generationRef.current) return
      setError(classifyMediaError(cause)); setStatus('error'); trackToolRejection(analytics, 'unsupported_input')
    }
  }

  function createSquareStream(): MediaStream | null {
    const source = videoRef.current
    const canvas = canvasRef.current
    if (!source || !canvas || source.videoWidth === 0 || source.videoHeight === 0 || typeof canvas.captureStream !== 'function') return null
    const size = Math.min(source.videoWidth, source.videoHeight)
    canvas.width = 720; canvas.height = 720
    const context = canvas.getContext('2d')
    if (!context) return null
    const sx = (source.videoWidth - size) / 2
    const sy = (source.videoHeight - size) / 2
    const draw = () => {
      context.save()
      context.clearRect(0, 0, 720, 720)
      if (mirror) { context.translate(720, 0); context.scale(-1, 1) }
      context.drawImage(source, sx, sy, size, size, 0, 0, 720, 720)
      context.restore()
      frameRef.current = window.requestAnimationFrame(draw)
    }
    let output: MediaStream | null = null
    try {
      draw()
      const input = streamRef.current
      if (!input) throw new Error('Source stream is unavailable')
      output = createCapturedMediaStream(() => canvas.captureStream(30), input)
      return output
    } catch (cause) {
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current)
      frameRef.current = null
      stopStream(output)
      throw cause
    }
  }

  function start() {
    revokeResult()
    try {
      const output = createSquareStream()
      if (!output) {
        cleanup(); setError('genericError'); setStatus('error')
        trackToolRejection(analytics, 'unsupported_output'); return
      }
      outputStreamRef.current = output
      const recorder = new MediaRecorder(output, mime ? { mimeType: mime } : undefined)
      failedRef.current = false
      chunksRef.current = []
      chunkTypesRef.current = []
      operationRef.current = startToolOperation(analytics)
      activeTimeRef.current.start(performance.now())
      recorder.ondataavailable = (event) => { if (event.data.size > 0) { chunksRef.current.push(event.data); chunkTypesRef.current.push(event.data.type) } }
      recorder.onerror = () => {
        if (failedRef.current) return
        failedRef.current = true
        operationRef.current?.error('processing_failed'); operationRef.current = null
        setError('genericError'); setStatus('error'); cleanup()
      }
      recorder.onstop = () => {
        if (failedRef.current || !mountedRef.current) return
        const actualMime = resolveRecorderMime('video', chunkTypesRef.current, recorder.mimeType, mime)
        if (!actualMime) {
          operationRef.current?.error('unsupported_output'); operationRef.current = null
          setError('genericError'); setStatus('error'); cleanup()
          return
        }
        const blob = new Blob(chunksRef.current, { type: actualMime })
        if (blob.size === 0) {
          operationRef.current?.error('processing_failed'); operationRef.current = null
          setError('genericError'); setStatus('error'); cleanup(); return
        }
        const nextUrl = URL.createObjectURL(blob)
        resultUrlRef.current = nextUrl
        setResultMime(actualMime); setUrl(nextUrl); setStatus('finished')
        operationRef.current?.success(activeTimeRef.current.elapsed(performance.now())); operationRef.current = null
        cleanup()
      }
      recorderRef.current = recorder
      recorder.start(250)
      setSeconds(0); setStatus('recording')
      timerRef.current = window.setInterval(() => {
        setSeconds(Math.min(MAX_SECONDS, Math.floor(activeTimeRef.current.elapsed(performance.now()) / 1000)))
      }, 250)
      scheduleAutoStop()
    } catch {
      const recorder = recorderRef.current
      if (recorder) {
        failedRef.current = true
        recorder.ondataavailable = null; recorder.onerror = null; recorder.onstop = null
        if (recorder.state !== 'inactive') {
          try { recorder.stop() } catch { /* setup already failed */ }
        }
      }
      const operation = operationRef.current
      if (operation) operation.error('processing_failed')
      else trackToolRejection(analytics, 'unsupported_output')
      operationRef.current = null
      cleanup(); setError('genericError'); setStatus('error')
    }
  }

  function stop() {
    if (timerRef.current !== null) { window.clearInterval(timerRef.current); timerRef.current = null }
    if (deadlineRef.current !== null) { window.clearTimeout(deadlineRef.current); deadlineRef.current = null }
    activeTimeRef.current.pause(performance.now())
    setSeconds(Math.min(MAX_SECONDS, Math.floor(activeTimeRef.current.elapsed(performance.now()) / 1000)))
    if (recorderRef.current?.state !== 'inactive') recorderRef.current?.stop()
  }

  function togglePause() {
    const recorder = recorderRef.current
    if (!recorder) return
    if (recorder.state === 'recording') {
      recorder.pause(); activeTimeRef.current.pause(performance.now())
      if (deadlineRef.current !== null) { window.clearTimeout(deadlineRef.current); deadlineRef.current = null }
      setSeconds(Math.floor(activeTimeRef.current.elapsed(performance.now()) / 1000)); setStatus('paused')
    } else if (recorder.state === 'paused') {
      recorder.resume(); activeTimeRef.current.resume(performance.now()); scheduleAutoStop(); setStatus('recording')
    }
  }

  function scheduleAutoStop() {
    if (deadlineRef.current !== null) window.clearTimeout(deadlineRef.current)
    const remaining = activeTimeRef.current.remaining(MAX_DURATION_MS, performance.now())
    deadlineRef.current = window.setTimeout(stop, remaining)
  }

  function reset() {
    generationRef.current += 1
    cleanup(); revokeResult()
    setResultMime(null); setSeconds(0); setError(null); setStatus('idle')
  }

  function revokeResult() {
    if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    resultUrlRef.current = null
    setUrl(null)
  }

  const needsCamera = status === 'idle' || status === 'requesting' || status === 'error'

  return (
    <Card className="overflow-hidden border-fuchsia-200/80 bg-card/95 shadow-lg dark:border-fuchsia-900/60">
      <CardContent className="space-y-4 p-4 sm:p-6">
        <div className="relative mx-auto aspect-square w-full max-w-60 overflow-hidden rounded-full border-4 border-primary/30 bg-muted shadow-inner sm:max-w-sm">
          {url ? (
            <video className="size-full object-cover" controls playsInline src={url} aria-label={t.common.preview} />
          ) : (
            <video ref={videoRef} className="size-full object-cover" style={{ transform: mirror ? 'scaleX(-1)' : undefined }} autoPlay muted playsInline onLoadedMetadata={() => setStatus((current) => current === 'requesting' ? 'ready' : current)} />
          )}
          {needsCamera && (
            <div className="absolute inset-0 grid place-items-center p-6">
              <Button className="min-h-11" disabled={status === 'requesting'} onClick={prepare}>
                {status === 'error' ? t.common.retry : t.circle.allow}
              </Button>
            </div>
          )}
        </div>
        <canvas ref={canvasRef} className="hidden" aria-hidden="true" />
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
          {status === 'ready' && <Button className="min-h-11" onClick={start}>{t.common.start}</Button>}
          {(status === 'recording' || status === 'paused') && <Button variant="outline" onClick={togglePause}>{status === 'paused' ? t.common.resume : t.common.pause}</Button>}
          {(status === 'recording' || status === 'paused') && <Button onClick={stop}>{t.common.stop}</Button>}
          {status === 'finished' && url && <Button asChild><a href={url} download={`mediatik-square-video.${extensionForMime(resultMime ?? 'video/webm')}`}>{t.common.download}</a></Button>}
          {status === 'finished' && <Button variant="outline" onClick={reset}>{t.common.reset}</Button>}
          {status !== 'finished' && (
            <label className="flex min-h-11 items-center justify-center gap-3 rounded-md border bg-background/70 px-3 text-sm">
              <input type="checkbox" checked={mirror} disabled={status === 'recording' || status === 'paused'} onChange={(event) => setMirror(event.target.checked)} className="size-4 accent-primary" />
              {t.circle.mirror}
            </label>
          )}
        </div>
        <RecorderStatus status={status} seconds={seconds} errorText={error ? t.circle.errors[error] : null} mime={resultMime ?? mime} />
        <p className="rounded-lg border border-amber-300/70 bg-amber-50 p-3 text-sm leading-6 text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">{t.circle.honest}</p>
        <p className="text-sm text-muted-foreground">{t.circle.permission} {t.circle.limit}. {t.common.noUpload}.</p>
      </CardContent>
    </Card>
  )
}
